// @flow
import { get } from 'lodash';

import graphQLClient from 'Utils/graphql-client/graphqlClient';
import {
  getPriceDetailFragment,
  getPaymentScheduleRequest,
  proposalFields,
  transportDetailsFragment,
  paymentProvidersRequest,
} from 'Utils/graphql-query-helper/booking/proposal/proposalQuery';
import { PROPOSAL_GET_BY_ID } from 'Utils/request/requestDictionary';

import validationSchemaAddressFragment from 'Store/validation-schema/validationSchema.address.fragment';
import { profileFragment } from 'Components/widgets/contact-info/ContactInfo.fragment';
import BookingAttendeesProfile from 'Components/widgets/contact-info/BookingAttendeesProfile.fragment';

export type ProposalsByIdQueryParam = {
  proposalsId: $ReadOnlyArray<string> | string,
  shouldRequestPayment: boolean,
  isJoyView?: boolean,
  adultsAttendeesIds?: Array<string>,
  kidsAttendeesIds?: Array<string>,
  fetchActivityCategories?: boolean,
};
const CHILDREN_ACTIVITY_IDENTIFIER = '_CHILD_';

export function getResort(fetchActivityCategories: boolean = false) {
  return `
    resort {
      id
      adultOnly
      badgeIcon
      comfort
      associatedResort{
        id
        name
      }
      destination {
        country
        area
      }
      facilities(types: ["CHILDREN"], withEquipments: true) {
        subtitle
        label
        equipments {
          id
          label
        }
      }
      childrenActivities: productActivities(idFilter: "${CHILDREN_ACTIVITY_IDENTIFIER}") {
        id
        subtitle
        label
        isIncluded
        description
        ageInMonths {
          min
          max
        }
        equipments {
          id
          label
        }
      }
      ${
        fetchActivityCategories
          ? `activityCategories {
        leisure {
          id
          label
          subCategories {
            id
            label
            imageUrl
            keywords
            activities {
              id
              label
              description
              isIncluded
              durationDescription
              practiceLevelDescription
              activityTags{
                id
                label
              }
              ageInMonths {
                min
                max
              }
            }
          }
        }
      }`
          : ''
      }
      offsetSell
      href
      image
      images
      isJoyview
      name
      seasonName
      seasons
      type
      category
      title
      accommodations {
        id
        roomType
        comfortId
        comfortType
        categoryId
        category
        connectingRoom
      }
    }`;
}

function getValidationSchema() {
  return `
    validationSchema(resource : "profile", fields: ["address"]) {
      ...ValidationSchemaAddress
    }
  `;
}

const getCxoAttendeesData = ({
  adultsAttendeesIds,
  kidsAttendeesIds,
}: {
  adultsAttendeesIds: $PropertyType<ProposalsByIdQueryParam, 'adultsAttendeesIds'>,
  kidsAttendeesIds: $PropertyType<ProposalsByIdQueryParam, 'kidsAttendeesIds'>,
}) =>
  `${
    adultsAttendeesIds && adultsAttendeesIds[0]
      ? `adultsAttendees: attendees(attendeesIds: ["${adultsAttendeesIds.join('","')}"]) {
          ...BookingAttendeesProfile
          birthdate: dateOfBirth
          email
          mobile: mobilePhoneNumber
          optinEmail
          optinSms
          optinPhone
          optinMail
          optinClubmedPlus
        }`
      : ''
  }
  ${
    // no need of birthday, we already have it
    kidsAttendeesIds && kidsAttendeesIds[0]
      ? `kidsAttendees: attendees(attendeesIds: ["${kidsAttendeesIds.join('","')}"]) {
          ...BookingAttendeesProfile
        }`
      : ''
  }
`;

function proposalsByIdQuery(params: ProposalsByIdQueryParam, appContext: AppContext): Promise<*> {
  const proposalsArgs = Array.isArray(params.proposalsId)
    ? [`ids: ["${params.proposalsId.join('","')}"]`]
    : [`ids: ["${params.proposalsId}"]`];

  return graphQLClient.runNoCacheQuery(
    `
    {
      proposals: proposalsById(${proposalsArgs.join(', ')}) {
        ... on ProposalsType {
          proposals {
            ${params.shouldRequestPayment ? getPaymentScheduleRequest(appContext) : ''}
            ${proposalFields({
              isJoyView: true,
              isCmta: appContext.isCmta,
            })}
          }
          ${getResort(params.fetchActivityCategories)}
        }
      }
      ${getValidationSchema()}
      ${appContext.isUserLogged ? 'profile {...BookingProfile}' : ''}
      ${getCxoAttendeesData(params)}
      ${params.shouldRequestPayment ? paymentProvidersRequest() : ''}
    }
    ${transportDetailsFragment}
    ${validationSchemaAddressFragment}
    ${getPriceDetailFragment(get(appContext, 'locale'))}
    ${appContext.isUserLogged ? profileFragment : ''}
    ${
      (params.adultsAttendeesIds && params.adultsAttendeesIds[0]) ||
      (params.kidsAttendeesIds && params.kidsAttendeesIds[0])
        ? BookingAttendeesProfile
        : ''
    }
  `,
    appContext,
    { requestName: PROPOSAL_GET_BY_ID },
  );
}

export { proposalsByIdQuery, getCxoAttendeesData };
